import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import AnalyticsApiService from '../../ApiServices/AnalyticsApiService/AnalyticsApiService';
import { toast } from 'react-toastify';
import PublisherApiService from '../../ApiServices/PublisherApiService/PublisherApiService';
import { useAuth } from "../Auth/AuthContext";
import WebsiteApiService from '../../ApiServices/WebsiteApiService/WebsiteApiService';
import AdunitApiService from '../../ApiServices/AnalyticsApiService/AdunitApiService';

const AddAnalyticsForm = ({ isOpen, onRequestClose, onSubmit }) => {
    const [formData, setFormData] = useState({
        date: new Date(),
        publisherId: '',
        publisher: '',
        websiteId: '',
        website: '',
        adUnits: '',
        adExchangeImpressions: '',
        adExchangeClicks: '',
        adExchangeCTR: '',
        adExchangeRevenue: '',
        adExchangePayout: '',
        adExchangeAvgECPM: '',
        adExchangeAdRequests: '',
        adExchangeMatchRate: '',
        adExchangeFillRate: ''
    });


    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [publishersData, setPublishers] = useState([]);
    const [webData, setWebsites] = useState([]);
    const [adunitsData, setadunitsData] = useState([]);
    const [revenuePayout, setrevenuePayout] = useState([]);

    const { authState } = useAuth();
    const user = authState.user;
    const role = authState.roles;
    const admin = role === "Admin";

    useEffect(() => {
        if (admin) {
            fetchPublishers();
            fetchAdunits();
        }
    }, [admin]);

    const fetchPublishers = async () => {
        try {
            const response = await PublisherApiService.get();
            setPublishers(response.data || []);
        } catch (error) {
            toast.error("Failed to load publishers");
        }
    };

    const fetchAdunits = async () => {
        setLoading(true);
        try {
            const response = await AdunitApiService.getAll();
            setadunitsData(response.data || []);
        } catch (error) {
            toast.error('Failed to fetch adunits.');
        } finally {
            setLoading(false);
        }
    };

    const fetchWebsites = async (publisherId) => {
        if (!publisherId) {
            setWebsites([]);
            return;
        }
        try {
            const response = await WebsiteApiService.GetAllWebByUserId(publisherId);
            setWebsites(response.data || []);
        } catch (error) {
            toast.error("Failed to load websites");
        }
    };

    const handlePublisherChange = (e) => {
        const publisherId = e.target.value;
        setFormData((prev) => ({ ...prev, publisher: publisherId, website: '' }));
        fetchWebsites(publisherId);
    };

    const handleInputChange = (e) => {
        if (e.target.name == "adExchangeRevenue") {
            const revenue = e.target.value;
            const payoutDeduction = revenue * 15 / 100; // 15% of revenue
            const payout = revenue - payoutDeduction;
            setrevenuePayout(payout);
        }
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleDateChange = (date) => {
        setFormData((prev) => ({ ...prev, date }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const obj = {
                publisherId: formData.publisher,
                publisher: formData.publisher,
                website: formData.website,
                websiteId: formData.website,
                date: formData.date.toISOString(),
                adUnits: formData.adunit,
                adUnit: formData.adunit,
                adExchangeImpressions: formData.adExchangeImpressions,
                adExchangeClicks: formData.adExchangeClicks,
                adExchangeCTR: formData.adExchangeCTR,
                adExchangeRevenue: formData.adExchangeRevenue,
                adExchangePayout: revenuePayout,
                adExchangeAvgECPM: formData.adExchangeAvgECPM,
                adExchangeAdRequests: formData.adExchangeAdRequests,
                adExchangeMatchRate: formData.adExchangeMatchRate,
                adExchangeFillRate: formData.adExchangeFillRate,
                country: formData.country,
            };
            await AnalyticsApiService.addAnalytics(obj);
            toast.success("Analytics data added successfully!");
            onSubmit();
            onRequestClose();

        } catch (error) {
            setError(error.message || 'Failed to add analytics data');
        } finally {
            setLoading(false);
        }
    };


    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            className="flex items-center justify-center min-h-screen p-4 animate-fadeIn mt-10"
        >
            <div className="bg-white rounded-2xl shadow-xl p-8 max-w-3xl w-full relative max-h-[90vh] overflow-hidden flex flex-col transition-all transform hover:scale-105 duration-300">

                {/* Close Button */}
                <button
                    onClick={onRequestClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-transform transform hover:scale-110"
                >
                    ✖
                </button>

                {/* Title */}
                <h2 className="text-3xl font-bold text-gray-700 text-center mb-4">
                    Add Analytics
                </h2>

                {error && <p className="text-red-500 mb-3 text-center">{error}</p>}

                {/* Scrollable Form */}
                <div className="overflow-y-auto flex-grow px-2" style={{ maxHeight: "70vh" }}>
                    <form onSubmit={handleSubmit} className="space-y-5">

                        {/* Date & Adunit Row */}
                        <div className="grid grid-cols-2 gap-6">

                            <div>
                                <label className="block text-gray-600 font-medium">Adunit</label>
                                <select
                                    name="adunit"
                                    value={formData.adunit}
                                    onChange={handleInputChange}
                                    className="border border-gray-300 rounded-md px-4 py-2 w-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                                    required
                                >
                                    <option value="">Select Adunit</option>
                                    {adunitsData.map((adunit, index) => (
                                        <option key={adunit.id || index} value={adunit.id}>{adunit.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-gray-600 font-medium">Country</label>
                                <select
                                    name="country"
                                    value={formData.country}
                                    onChange={handleInputChange}
                                    className="border border-gray-300 rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all hover:shadow-md"
                                    required
                                >
                                    <option value="">Select Country</option>
                                    {[
                                        "United States", "United Kingdom", "Canada", "Australia",
                                        "Germany", "France", "India", "China", "Japan",
                                        "Brazil", "Mexico", "Italy", "Spain", "Netherlands", "South Korea"
                                    ].map((country, index) => (
                                        <option key={index} value={country}>{country}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Publisher & Website Row */}
                        <div className="grid grid-cols-2 gap-6">
                            <div>
                                <label className="block text-gray-600 font-medium">Publisher</label>
                                <select
                                    name="publisher"
                                    value={formData.publisher}
                                    onChange={handlePublisherChange}
                                    className="border border-gray-300 rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all hover:shadow-md"
                                    required
                                >
                                    <option value="">Select Publisher</option>
                                    {publishersData.map((pub, index) => (
                                        <option key={pub.id || index} value={pub.id}>{pub.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-gray-600 font-medium">Website</label>
                                <select
                                    name="website"
                                    value={formData.website}
                                    onChange={handleInputChange}
                                    className="border border-gray-300 rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all hover:shadow-md"
                                    required
                                >
                                    <option value="">Select Website</option>
                                    {webData.map((site, index) => (
                                        <option key={site.websiteId || index} value={site.websiteId}>{site.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Other Fields */}
                        <div className="grid grid-cols-2 gap-6">
                            {[
                                'adExchangeImpressions',
                                'adExchangeClicks',
                                'adExchangeCTR',
                                'adExchangeRevenue',
                                'adExchangeAvgECPM',
                                'adExchangeAdRequests',
                                'adExchangeMatchRate',
                                'adExchangeFillRate'
                            ].map((field) => (
                                <div key={field}>
                                    <label className="block text-gray-600 font-medium capitalize">{field.replace(/([A-Z])/g, ' $1')}</label>
                                    <input
                                        name={field}
                                        type="number"
                                        value={formData[field]}
                                        onChange={handleInputChange}
                                        className="border border-gray-300 rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all hover:shadow-md"
                                        required
                                    />
                                </div>
                            ))}
                            <div key="adExchangePayout">
                                    <label className="block text-gray-600 font-medium capitalize">Payout</label>
                                    <input
                                        name="adExchangePayout"
                                        readOnly="true"
                                        type="number"
                                        value={revenuePayout}
                                        onChange={handleInputChange}
                                        className="border border-gray-300 rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all hover:shadow-md"
                                        required
                                    />
                                </div>
                            <div>
                                <label className="block text-gray-600 font-medium">Date</label>
                                <DatePicker
                                    selected={formData.date}
                                    onChange={handleDateChange}
                                    className="border border-gray-300 rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all hover:shadow-md"
                                    required
                                />
                            </div>
                        </div>

                        {/* Submit & Cancel Buttons */}
                        <div className="flex justify-center space-x-4 pt-4">
                            <button
                                type="button"
                                onClick={onRequestClose}
                                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition-all transform hover:scale-105 duration-300"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-2 rounded-lg hover:shadow-lg transition-all transform hover:scale-105 duration-300"
                                disabled={loading}
                            >
                                {loading ? 'Submitting...' : 'Submit'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </Modal>


    );
};

export default AddAnalyticsForm;
